/**
 * Google Address Autocomplete Component
 * ======================================
 * Uses Google Places API to autocomplete addresses and auto-fill form fields
 */

import React, { useEffect, useRef, useState } from 'react';

interface GoogleAddressAutocompleteProps {
  label?: string;
  value: string;
  onChange: (value: string) => void;
  onPlaceSelected: (place: {
    address: string;
    city: string;
    state: string;
    postalCode: string;
    country: string;
  }) => void;
  error?: string;
  placeholder?: string;
}

// Extend Window interface to include google
declare global {
  interface Window {
    google: any;
    initGoogleMaps: () => void;
  }
}

export const GoogleAddressAutocomplete: React.FC<GoogleAddressAutocompleteProps> = ({
  label = 'Street Address',
  value,
  onChange,
  onPlaceSelected,
  error,
  placeholder = '123 Main St',
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const autocompleteRef = useRef<any>(null);
  const [isLoaded, setIsLoaded] = useState(false);

  // Load Google Maps API
  useEffect(() => {
    const apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;

    if (!apiKey) {
      console.error('Google Maps API key not found in environment variables');
      return;
    }

    // Check if Google Maps is already loaded
    if (window.google && window.google.maps && window.google.maps.places) {
      setIsLoaded(true);
      return;
    }

    // Check if script is already being loaded
    if (document.querySelector('script[src*="maps.googleapis.com"]')) {
      // Wait for it to load
      window.initGoogleMaps = () => {
        setIsLoaded(true);
      };
      return;
    }

    // Load the script
    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places&callback=initGoogleMaps`;
    script.async = true;
    script.defer = true;

    window.initGoogleMaps = () => {
      setIsLoaded(true);
    };

    script.onerror = () => {
      console.error('Failed to load Google Maps API');
    };

    document.head.appendChild(script);

    return () => {
      // Cleanup callback
      delete window.initGoogleMaps;
    };
  }, []);

  // Initialize autocomplete when loaded
  useEffect(() => {
    if (!isLoaded || !inputRef.current || autocompleteRef.current) {
      return;
    }

    try {
      // Initialize Google Places Autocomplete
      const autocomplete = new window.google.maps.places.Autocomplete(inputRef.current, {
        types: ['address'],
        componentRestrictions: { country: ['us', 'ca', 'gb', 'au', 'in'] },
      });

      // Listen for place selection
      autocomplete.addListener('place_changed', () => {
        const place = autocomplete.getPlace();

        if (!place.address_components) {
          return;
        }

        // Extract address components
        let address = '';
        let city = '';
        let state = '';
        let postalCode = '';
        let country = '';

        // Parse address components
        place.address_components.forEach((component: any) => {
          const types = component.types;

          if (types.includes('street_number')) {
            address = component.long_name + ' ' + address;
          }
          if (types.includes('route')) {
            address += component.long_name;
          }
          if (types.includes('locality')) {
            city = component.long_name;
          }
          if (types.includes('administrative_area_level_1')) {
            state = component.short_name; // Use short name for state (e.g., "CA" instead of "California")
          }
          if (types.includes('postal_code')) {
            postalCode = component.long_name;
          }
          if (types.includes('country')) {
            country = component.short_name; // Use country code (e.g., "US")
          }
        });

        // Update the input value
        onChange(address.trim());

        // Call the callback with parsed data
        onPlaceSelected({
          address: address.trim(),
          city,
          state,
          postalCode,
          country,
        });
      });

      autocompleteRef.current = autocomplete;
    } catch (error) {
      console.error('Error initializing Google Places Autocomplete:', error);
    }
  }, [isLoaded, onChange, onPlaceSelected]);

  return (
    <div>
      {label && (
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {label}
        </label>
      )}
      <input
        ref={inputRef}
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className={`w-full px-3 py-2 border ${
          error ? 'border-red-300' : 'border-gray-300'
        } rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
          !isLoaded ? 'bg-gray-50' : ''
        }`}
        disabled={!isLoaded}
      />
      {!isLoaded && (
        <p className="mt-1 text-xs text-gray-500">Loading address search...</p>
      )}
      {error && <p className="mt-1 text-sm text-red-600">{error}</p>}
    </div>
  );
};
